import React, { useContext, useEffect } from 'react'
import useMemberTable from '../context/MemberTableContext'
import MicroModal from 'micromodal'
import { ColumnDef, ColumnFiltersState, SortingState } from '@tanstack/react-table'
import { PendingInviteTableData, PendingInviteTableType } from '../data/PendingInviteTableData'
import { useParams } from 'react-router-dom'
import TableCheckbox from '../../../../common/checkbox/TableCheckbox'
import { getExportHeaders } from '../../ReportingGroup/types/tableHeaders'
import TanstackContent from '../../Table/Tanstack/TanstackContent'
import ModalInviteLink from './ModalInviteLink'
import { modalPendingInviteTblBtnId } from './Utils'
import { modalConfirmationMessageId, notAllowedUserMembershipStatus, UserBadge } from '../Members/Utils'
import ModalConfirmation from '../Members/ModalConfirmation'
import BulkModalConfirmation from '../Header/Actions/BulkModalConfirmation'
import { customFilterFn } from '../Header/Utils'
import { TeamContext } from '../../MenuTabs/MenuTabs'
import AddMemberModal from '../form/AddMemberModal'
import { getPrettyDate } from '../../ReportingGroup/utils'
import MembersTableButton from '../Members/MembersTableButton'

const PendingInvitesTable = ({ pendingInvitesData, teamType }) => {

  const [ columnFilters, setColumnFilters ] = React.useState<ColumnFiltersState>([])
  const { setUseHeader } = useMemberTable()
  const [ rowSelection, setRowSelection ] = React.useState({})
  const [ globalFilter, setGlobalFilter ] = React.useState('')
  const [ sorting, setSorting ] = React.useState<SortingState>([])
  const [ data, setData ] = React.useState( () => PendingInviteTableData( pendingInvitesData ) )
  const { refetchPendingInvites } = useMemberTable()

  const params = useParams()
  const teamId = params.team_id ?? 0

  useEffect(() => {
    MicroModal.init()
    setUseHeader([ 'all' ])
  }, [])

  const columns = React.useMemo<ColumnDef<PendingInviteTableType>[]>(
    () => [
      {     
        id: 'select',
        header: ({ table }) => (
          <TableCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: 'columns',
        columns: [
          {
            id: 'invitationId',
            accessorKey: 'invitationId',
            prettyName: 'Invitation ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>INVITATION ID</span>,
          },
          {
            id: 'invitationTeamId',
            accessorKey: 'invitationTeamId',
            prettyName: 'Invitation Team ID',
            show: false,
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>INVITATION TEAM ID</span>,
          },
          {
            id: 'invitationName',
            accessorKey: 'invitationName',
            prettyName: 'Invitation Name',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>NAME</span>,
          },
          {
            id: 'invitationEmail',
            accessorKey: 'invitationEmail',
            prettyName: 'Invitation Email',
            cell: info => info.getValue(),
            footer: props => props.column.id,
            header: () => <span>EMAIL</span>,
          },
          {
            id: 'invitationDateSent',
            accessorKey: 'invitationDateSent',
            prettyName: 'Invitation Date Sent',
            cell: info => (
              <span className='msync-space-nowrap'>{ getPrettyDate( null, 'all', info.getValue(), 'short' ) }</span>
            ),
            footer: props => props.column.id,
            header: () => <span>DATE SENT</span>,
          },
          {
            id: 'invitationRole',
            accessorKey: 'invitationRole',
            prettyName: 'Invitation Role',
            cell: info => (
              <UserBadge role={ info.getValue() } />
            ),
            footer: props => props.column.id,
            header: () => <span>ROLE</span>,
            filterFn: customFilterFn
          }, 
          {
            id: 'actions',
            accessorKey: 'actions',
            enableSorting: false,
            prettyName: 'Actions',
            cell: (info) => (
              teamType === 'wooteams' ? <Actions info={ info.row.original } /> : ''
            ),
            footer: props => props.column.id,
            header: () => <span>ACTIONS</span>,
            meta: {
              className: "msync-custom-member-action-header",
            },
          },
        ]
      }
    ],
    []
  )

  const exportHeaders = getExportHeaders('team-members')
  const { teamData } = useContext(TeamContext)

  const Actions = ({ info }) => {
    return (
      <div className='msync-table-action-column'>
        { info.invitationRole.toLocaleLowerCase() === 'member' && 
          <MembersTableButton
            btnName='Set as manager'
            id={ modalConfirmationMessageId }
            info={{ ...info, modal: modalPendingInviteTblBtnId.setAsManager }}
          />
        }
        { info.invitationRole.toLocaleLowerCase() === 'manager' && 
          <MembersTableButton
            btnName='Set as member'
            id={ modalConfirmationMessageId }
            info={{ ...info, modal: modalPendingInviteTblBtnId.setAsMember }}
          />
        }
        <MembersTableButton
          btnName='Resend'
          id={ modalConfirmationMessageId }
          info={{ ...info, modal: modalPendingInviteTblBtnId.resendInvitation }}
        />
        <MembersTableButton
          btnName='Revoke'
          btnClassName='msync-button--is-theme-destructive'
          id={ modalConfirmationMessageId }
          info={{ ...info, modal: modalPendingInviteTblBtnId.revokeInvitation }}
        />
      </div>
    )
  }

  return (
    <>
      <TanstackContent
        data={ data }
        columns={ columns }
        rowSelection={ rowSelection }
        setRowSelection={ setRowSelection }
        sorting={ sorting }
        setSorting={ setSorting }
        globalFilter={ globalFilter }
        setGlobalFilter={ setGlobalFilter }
        exportHeaders={ exportHeaders }
        from='Pending Invites Table'
        context='Pending Invites'
        hideFilters={ true }
        columnFilters={ columnFilters }
        setColumnFilters={ setColumnFilters }
      >
        <BulkModalConfirmation key='modal-pending-invites-bulk-confirmation-message' />
        <ModalConfirmation key='modal-pending-invites-confirmation-message'/>
        <ModalInviteLink />
        {
          ! notAllowedUserMembershipStatus.includes(teamData.current_user_membership?.membership_status) &&
          <AddMemberModal
            teamId={ teamId }
            isAdmin={ teamData.is_user_admin }
            modalOption={ teamData.members_modal_option }
            refetchAdded={ refetchPendingInvites }
            refetchInvited={ refetchPendingInvites }
          />
        }
      </TanstackContent>
    </>
  )
}

export default PendingInvitesTable
